import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Spin } from 'antd';
import PaymentService from '../services/paymentService';
import {
    LoadingOutlined, CheckCircleFilled, CloseCircleFilled,
    ClockCircleFilled, ExclamationCircleFilled, HeartOutlined,
    CrownOutlined, CalendarOutlined, DollarOutlined,
} from '@ant-design/icons';

const STATUS_CONFIG = {
    success: {
        icon: <CheckCircleFilled style={{ fontSize: 72, color: '#FFFFFF' }} />,
        title: 'Payment Successful!',
        subtitle: 'Your subscription is now active. Welcome to the premium experience.',
        headerBg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
        badge: { bg: 'rgba(16,185,129,0.1)', color: '#059669', text: 'PAID' },
    },
    pending: {
        icon: <ClockCircleFilled style={{ fontSize: 72, color: '#FFFFFF' }} />,
        title: 'Payment Pending',
        subtitle: 'Your payment is being verified. This usually takes a few minutes.',
        headerBg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
        badge: { bg: 'rgba(245,158,11,0.1)', color: '#D97706', text: 'PENDING' },
    },
    failed: {
        icon: <CloseCircleFilled style={{ fontSize: 72, color: '#FFFFFF' }} />,
        title: 'Payment Failed',
        subtitle: 'We couldn\'t process your payment. Please try again.',
        headerBg: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
        badge: { bg: 'rgba(239,68,68,0.1)', color: '#DC2626', text: 'FAILED' },
    },
    cancelled: {
        icon: <CloseCircleFilled style={{ fontSize: 72, color: '#FFFFFF' }} />,
        title: 'Payment Cancelled',
        subtitle: 'You cancelled the payment. Your plan has not been changed.',
        headerBg: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
        badge: { bg: 'rgba(107,114,128,0.1)', color: '#4B5563', text: 'CANCELLED' },
    },
    error: {
        icon: <ExclamationCircleFilled style={{ fontSize: 72, color: '#FFFFFF' }} />,
        title: 'Something Went Wrong',
        subtitle: 'An unexpected error occurred. Please contact support.',
        headerBg: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
        badge: { bg: 'rgba(239,68,68,0.1)', color: '#DC2626', text: 'ERROR' },
    },
};

const Row = ({ label, value, bold }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748B' }}>{label}</span>
        <span style={{ fontSize: '0.9rem', fontWeight: bold ? 700 : 500, color: '#1A1A2E' }}>{value}</span>
    </div>
);

const PaymentReturn = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const orderId = params.get('order_id');
        if (!orderId) { setStatus('error'); setLoading(false); return; }
        verify(orderId);
    }, [location]);

    // Auto-go to dashboard after success
    useEffect(() => {
        if (status !== 'success') return;
        const t = setTimeout(() => navigate('/subscription-management'), 6000);
        return () => clearTimeout(t);
    }, [status, navigate]);

    const verify = async (orderId) => {
        try {
            const res = await PaymentService.handleReturnUrl(orderId);
            const d = res.data;
            setData(d);

            const hhStatus = d?.hirehike_order_details?.status;
            const dbStatus = d?.status;

            if (hhStatus === 'paid' || dbStatus === 'success') {
                setStatus('success');
            } else if (dbStatus === 'failed' || hhStatus === 'failed') {
                setStatus('failed');
            } else if (dbStatus === 'cancelled' || hhStatus === 'cancelled' || hhStatus === 'expired') {
                setStatus('cancelled');
            } else {
                setStatus('pending');
            }
        } catch {
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg,#FFFAFC 0%,#FDF2F8 100%)',
                fontFamily: 'Calibri, sans-serif', gap: '20px',
            }}>
                <div style={{
                    width: 72, height: 72, borderRadius: '20px',
                    background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(173,20,87,0.35)',
                }}>
                    <HeartOutlined style={{ color: '#FFFFFF', fontSize: '1.8rem' }} />
                </div>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 36, color: '#AD1457' }} spin />} />
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1A1A2E', marginBottom: 6 }}>
                        Verifying your payment…
                    </div>
                    <div style={{ fontSize: '0.88rem', color: '#64748B' }}>
                        Please wait, this only takes a moment
                    </div>
                </div>
            </div>
        );
    }

    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.error;
    const plan = data?.subscription_plan;
    const sub = data?.subscription;

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg,#FFFAFC 0%,#FDF2F8 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '24px 16px',
            fontFamily: 'Calibri, sans-serif',
        }}>
            {/* Brand */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
                <div style={{
                    width: 40, height: 40, borderRadius: '10px',
                    background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(173,20,87,0.3)',
                }}>
                    <HeartOutlined style={{ color: '#FFFFFF', fontSize: '1.1rem' }} />
                </div>
                <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#AD1457' }}>Oottan</span>
            </div>

            {/* Card */}
            <div style={{
                width: '100%', maxWidth: 480,
                background: 'white', borderRadius: '24px',
                boxShadow: '0 20px 60px rgba(173,20,87,0.12), 0 4px 16px rgba(0,0,0,0.06)',
                overflow: 'hidden',
            }}>
                {/* Header banner */}
                <div style={{
                    background: cfg.headerBg,
                    padding: '36px 24px 28px',
                    textAlign: 'center',
                }}>
                    <div style={{ marginBottom: 16 }}>{cfg.icon}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#FFFFFF', marginBottom: 8 }}>
                        {cfg.title}
                    </div>
                    <div style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
                        {cfg.subtitle}
                    </div>
                </div>

                <div style={{ padding: '28px 24px' }}>
                    {/* Order details */}
                    {data && (
                        <div style={{
                            background: '#F8FAFC', borderRadius: '14px',
                            padding: '4px 16px', marginBottom: 20,
                            border: '1px solid #F1F5F9',
                        }}>
                            {data.order_id && (
                                <Row label="Order ID" value={data.order_id} />
                            )}
                            {data.amount && (
                                <Row
                                    label="Amount"
                                    value={`₹${parseFloat(data.amount).toLocaleString('en-IN')} ${data.currency || 'INR'}`}
                                    bold
                                />
                            )}
                            {plan?.name && (
                                <Row label="Plan" value={plan.name} />
                            )}
                            {sub?.end_date && (
                                <Row
                                    label="Valid Until"
                                    value={new Date(sub.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                                />
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                                <span style={{ fontSize: '0.85rem', color: '#64748B' }}>Status</span>
                                <span style={{
                                    background: cfg.badge.bg, color: cfg.badge.color,
                                    borderRadius: '20px', padding: '3px 12px',
                                    fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.5px',
                                }}>
                                    {cfg.badge.text}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Success extras */}
                    {status === 'success' && (
                        <div style={{
                            background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)',
                            borderRadius: '12px', padding: '14px 16px', marginBottom: 20,
                            display: 'flex', gap: 10, alignItems: 'flex-start',
                        }}>
                            <CrownOutlined style={{ color: '#059669', fontSize: '1rem', marginTop: 2, flexShrink: 0 }} />
                            <div>
                                <div style={{ fontWeight: 700, color: '#059669', fontSize: '0.88rem', marginBottom: 2 }}>
                                    Subscription activated!
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#047857', lineHeight: 1.4 }}>
                                    You'll be redirected to your subscription details in a few seconds.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pending note */}
                    {status === 'pending' && (
                        <div style={{
                            background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
                            borderRadius: '12px', padding: '14px 16px', marginBottom: 20,
                            display: 'flex', gap: 10, alignItems: 'flex-start',
                        }}>
                            <CalendarOutlined style={{ color: '#D97706', fontSize: '1rem', marginTop: 2, flexShrink: 0 }} />
                            <div style={{ fontSize: '0.8rem', color: '#92400E', lineHeight: 1.5 }}>
                                Payment confirmation can take up to 5 minutes. Your subscription will activate automatically once confirmed. If it doesn't, contact support with your Order ID.
                            </div>
                        </div>
                    )}

                    {/* Failed note */}
                    {(status === 'failed' || status === 'error') && (
                        <div style={{
                            background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
                            borderRadius: '12px', padding: '14px 16px', marginBottom: 20,
                            display: 'flex', gap: 10, alignItems: 'flex-start',
                        }}>
                            <DollarOutlined style={{ color: '#DC2626', fontSize: '1rem', marginTop: 2, flexShrink: 0 }} />
                            <div style={{ fontSize: '0.8rem', color: '#7F1D1D', lineHeight: 1.5 }}>
                                No amount has been deducted. You can try again or contact support if the issue persists.
                            </div>
                        </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {status === 'success' && (
                            <>
                                <Button
                                    type="primary" block size="large"
                                    onClick={() => navigate('/subscription-management')}
                                    style={{
                                        height: 48, borderRadius: '12px', fontWeight: 700,
                                        fontSize: '0.95rem',
                                        background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                                        border: 'none',
                                    }}
                                >
                                    View My Subscription
                                </Button>
                                <Button
                                    block size="large"
                                    onClick={() => navigate('/dashboard')}
                                    style={{ height: 48, borderRadius: '12px', fontWeight: 600, fontSize: '0.95rem' }}
                                >
                                    Go to Dashboard
                                </Button>
                            </>
                        )}

                        {status === 'pending' && (
                            <>
                                <Button
                                    type="primary" block size="large"
                                    onClick={() => navigate('/dashboard')}
                                    style={{
                                        height: 48, borderRadius: '12px', fontWeight: 700,
                                        fontSize: '0.95rem',
                                        background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                                        border: 'none',
                                    }}
                                >
                                    Go to Dashboard
                                </Button>
                                <Button
                                    block size="large"
                                    onClick={() => navigate('/contact-us')}
                                    style={{ height: 48, borderRadius: '12px', fontWeight: 600, fontSize: '0.95rem' }}
                                >
                                    Contact Support
                                </Button>
                            </>
                        )}

                        {(status === 'failed' || status === 'cancelled' || status === 'error') && (
                            <>
                                <Button
                                    type="primary" block size="large"
                                    onClick={() => navigate('/pricing')}
                                    style={{
                                        height: 48, borderRadius: '12px', fontWeight: 700,
                                        fontSize: '0.95rem',
                                        background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                                        border: 'none',
                                    }}
                                >
                                    Try Again
                                </Button>
                                <Button
                                    block size="large"
                                    onClick={() => navigate('/contact-us')}
                                    style={{ height: 48, borderRadius: '12px', fontWeight: 600, fontSize: '0.95rem' }}
                                >
                                    Contact Support
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ marginTop: 20, fontSize: '0.78rem', color: '#94A3B8', textAlign: 'center' }}>
                Need help? Email us at{' '}
                <a href="mailto:info@oottan.com" style={{ color: '#AD1457' }}>info@oottan.com</a>
            </div>
        </div>
    );
};

export default PaymentReturn;
